// useFormShell — the shared form-body core (ADR-038 §2/§3): ONE RHF + Zod body
// reused across every container (drawer, dialog, inline section, wizard, page);
// the container is the only variable. This inverts the dev-era shape (shared
// chrome, re-rolled body), which is why that wrapper never generalized.
//
// Owns the pieces ADR-038 calls "the body":
//   • RHF + the Zod resolver (ADR-008 — never manual validation; schemas from
//     packages/schemas, ADR-009).
//   • THE SEED (ADR-084 §6) — the values the form OPENED with, read from the row
//     at mount and at every `reseed()` (an open), never reactively: a background
//     refetch of the row never moves the baseline under the typing. The row's
//     stamp is taken with them, as ONE snapshot, and handed to the save
//     (`row_version`, ADR-084 §3) — never the cache's latest at save time, which
//     would carry a colleague's version and pass over their change. `changed`
//     is the diff against those opened values (never against a live prop).
//   • THE REFUSAL LINE (useRefusalLine) — a refused save becomes one line above
//     the fields: a stale save offers Load the latest, which re-reads the row and
//     re-seeds the fields the person did not touch, the typed ones kept (`keepTyped`
//     — a list the person touched stays whole, its rows positional; an object keeps
//     only its touched leaves); a row gone under the form says so with
//     Close; a busy answer says nothing (the door's toast); the rest the code's
//     words. Never a toast, never a navigation.
//   • the in-flight signal — RHF's own formState.isSubmitting, no parallel flag.
//   • the structural dirty guard's router-AGNOSTIC half: dirty detection
//     (formState.isDirty — never manual), the discard-intent handshake
//     (requestLeave → ConfirmDialog when dirty), and the beforeunload guard for
//     hard close / refresh.
//
// The dirty guard's ROUTE-LEAVE half (in-app navigation) is NOT here — it rides
// useBlocker, which throws outside a data router, so a shell calling it could not
// render in a Workbench story under the MemoryRouter decorator. It lives in
// useRouteLeaveGuard, which the page / inline / wizard containers opt into; the
// drawer never changes the route, so it needs only this hook.

import { useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  useForm,
  type DefaultValues,
  type FieldValues,
  type Path,
  type PathValue,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import { formResolver } from '../../../lib/validation.js';
import { useBeforeUnload } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { statusOf } from '../../../lib/refusal.js';
import { ConfirmDialog } from '../../ui/ConfirmDialog.js';
import { useRefusalLine, type RefusalLineState } from './useRefusalLine.js';

/** A row a form opens on: its stamp rides the read (ADR-084 §3); a row with one writer carries none. */
export interface StampedRow {
  row_version?: number;
}

export interface SubmitContext<TValues extends FieldValues> {
  /** The row's stamp as the form opened (or last loaded the latest) — sent back as `row_version`;
   *  undefined on a create or an unstamped row. */
  stamp: number | undefined;
  /** What the person changed since the form opened — the diff against the OPENED values, by field. */
  changed: Partial<TValues>;
}

export interface UseFormShellOptions<TValues extends FieldValues, TRow extends StampedRow = StampedRow> {
  /** Zod schema from packages/schemas (ADR-009) — drives validation AND the value
   *  type. Typed as input === output (`<TValues, TValues>`): a form schema
   *  validates to the same shape its fields hold (no coercion / retyping). */
  schema: z.ZodType<TValues, TValues>;
  /** The row the form opens on — null or absent for a create. Read at mount and at
   *  every reseed (an open); a refetch while the form is open changes nothing. */
  row?: TRow | null;
  /** The seed from the row: the values the form opens with, its baseline. A create: `() => EMPTY`. */
  values: (row: TRow | null) => DefaultValues<TValues>;
  /** Load the latest: the fresh row, or null when it is gone — a QUIET read (`quietNotFound`), so a row gone
   *  meanwhile answers 404 to the form (gone) instead of taking the person to the not-found page. */
  reload?: () => Promise<TRow | null>;
  /** Runs on a valid submit with the stamp and the diff. Throw to surface a refusal; resolve to succeed. */
  onSubmit: (values: TValues, ctx: SubmitContext<TValues>) => void | Promise<void>;
  /** Runs after onSubmit resolves (close the drawer, toast, navigate). The form is
   *  already reset to the submitted values by then, so a navigation here is clean. */
  onSuccess?: () => void;
}

export interface UseFormShellReturn<TValues extends FieldValues> {
  /** The RHF instance — fields register/Controller against this (FRONTEND-PATTERNS §Forms). */
  form: UseFormReturn<TValues>;
  /** Bind to `<form onSubmit>`; wraps RHF handleSubmit + the refusal mapping. */
  onFormSubmit: ReturnType<UseFormReturn<TValues>['handleSubmit']>;
  /** Associates the footer's submit Button with the body `<form>` across the
   *  body/footer split a drawer or page imposes (`<Button form={formId} type="submit">`). */
  formId: string;
  /** The refusal line's state — FormBody draws it; a container's own surface draws `<RefusalLine line={shell.line} />`. */
  line: RefusalLineState;
  /** Hand a write made outside the submit (a wizard's step commit) to the line. */
  refuse: (err: unknown) => void;
  /** Re-read the seed: the containers call it on every open (a drawer kept mounted), an inline section on Edit. */
  reseed: () => void;
  /** The row left under the form: fields read-only, Save disabled, Close the one act. */
  gone: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
  /** The guarded leave gate: clean → leave now; dirty → open the discard prompt.
   *  `onCancelLeave` runs if the user keeps editing (e.g. a route blocker's reset). */
  requestLeave: (onConfirmLeave: () => void, onCancelLeave?: () => void) => void;
  /** Leave dropping the typing without the prompt — the gone line's Close (nothing is left to save it to). */
  discardAndLeave: (leave: () => void) => void;
  /** The discard ConfirmDialog element — render once per shell, as a SIBLING of the
   *  container (so closing the container on discard doesn't unmount the dialog). */
  discardDialog: ReactNode;
}

/** A field the person touched, anywhere beneath it (RHF's dirty tree: `true` at a leaf, arrays and objects above). */
function touched(dirty: unknown): boolean {
  if (dirty === true) return true;
  if (Array.isArray(dirty)) return dirty.some(touched);
  if (dirty !== null && typeof dirty === 'object') return Object.values(dirty).some(touched);
  return false;
}

/** Load the latest's merge, field by field: what the person did not touch takes the fresh value; what they typed
 *  stays. A LIST they touched stays whole — its rows are positional (one removed, one added, the order moved), so a
 *  merge by index would splice their rows into the colleague's; an object keeps only its touched leaves. */
export function keepTyped(fresh: unknown, current: unknown, dirty: unknown): unknown {
  if (!touched(dirty)) return fresh;
  if (dirty === true || Array.isArray(dirty) || Array.isArray(current) || current === null || typeof current !== 'object') return current;
  const out: Record<string, unknown> = { ...((fresh ?? {}) as Record<string, unknown>) };
  for (const [key, d] of Object.entries(dirty as Record<string, unknown>)) {
    out[key] = keepTyped((fresh as Record<string, unknown> | null | undefined)?.[key], (current as Record<string, unknown>)[key], d);
  }
  return out;
}

/** The seed a row gives: the values the form opens with and the stamp they came with — ONE snapshot. */
function seedFrom<TValues extends FieldValues, TRow extends StampedRow>(
  o: UseFormShellOptions<TValues, TRow>,
  row: TRow | null,
): { values: DefaultValues<TValues>; stamp: number | undefined; edit: boolean } {
  return { values: o.values(row), stamp: row?.row_version, edit: row !== null };
}

export function useFormShell<TValues extends FieldValues, TRow extends StampedRow = StampedRow>(
  options: UseFormShellOptions<TValues, TRow>,
): UseFormShellReturn<TValues> {
  const { t } = useTranslation('common');
  const formId = useId();
  // The options as of the last commit — a reseed (a container's open effect) or a reload (an act) reads the row the
  // host holds NOW, never a stale closure. A layout effect, so a container's own passive open effect sees it current.
  const optionsRef = useRef(options);
  useLayoutEffect(() => {
    optionsRef.current = options;
  });

  // ONE snapshot at mount: the opened values and the stamp they came with.
  const [opened] = useState(() => seedFrom(options, options.row ?? null));
  const stampRef = useRef<number | undefined>(opened.stamp);
  // An edit (a form opened on a row) — taken with the seed, so a row that leaves a live list meanwhile cannot turn
  // the form into a create at refusal time.
  const [edit, setEdit] = useState(opened.edit);

  const form = useForm<TValues>({
    resolver: formResolver(options.schema),
    defaultValues: opened.values,
  });

  const line = useRefusalLine({
    edit,
    reload: options.reload
      ? async () => {
          const o = optionsRef.current;
          let fresh: TRow | null;
          try {
            fresh = await o.reload!();
          } catch (err) {
            if (statusOf(err) === 404) return false;
            throw err;
          }
          if (fresh === null) return false;
          const seed = seedFrom(o, fresh);
          stampRef.current = seed.stamp;
          // The baseline moves to the fresh row; what the person typed is put back over it, still dirty.
          const dirty = form.formState.dirtyFields as Record<string, unknown>;
          const typed = form.getValues() as Record<string, unknown>;
          form.reset(seed.values);
          for (const key of Object.keys(dirty)) {
            if (!touched(dirty[key])) continue;
            const kept = keepTyped((seed.values as Record<string, unknown>)[key], typed[key], dirty[key]);
            form.setValue(key as Path<TValues>, kept as PathValue<TValues, Path<TValues>>, { shouldDirty: true });
          }
          return true;
        }
      : undefined,
  });

  // Read in render so the component subscribes to the RHF formState proxy: the
  // guards key on the live values, the diff on the live dirty set (this render's —
  // the submit handler below is remade with it).
  const { isDirty, isSubmitting, dirtyFields } = form.formState;

  const changedOf = (values: TValues): Partial<TValues> => {
    const dirty = dirtyFields as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(dirty)) if (dirty[key]) out[key] = (values as Record<string, unknown>)[key];
    return out as Partial<TValues>;
  };

  const onValid: SubmitHandler<TValues> = async (values) => {
    line.dismiss();
    try {
      await options.onSubmit(values, { stamp: stampRef.current, changed: changedOf(values) });
      // New baseline → isDirty false. Without this, the form's OWN post-submit
      // navigation (onSuccess) would trip the route blocker + beforeunload guard.
      form.reset(values);
      options.onSuccess?.();
    } catch (err) {
      line.refuse(err);
    }
  };

  const { dismiss } = line;
  const reseed = useCallback(() => {
    const o = optionsRef.current;
    const seed = seedFrom(o, o.row ?? null);
    stampRef.current = seed.stamp;
    setEdit(seed.edit);
    form.reset(seed.values);
    dismiss();
  }, [form, dismiss]);

  // The dirty guard's hard-close / refresh half (router-agnostic — just a
  // beforeunload listener; React Router's useBeforeUnload needs no data router).
  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (isDirty) {
          e.preventDefault();
          e.returnValue = '';
        }
      },
      [isDirty],
    ),
  );

  // The discard-intent handshake. One ConfirmDialog serves every leave source —
  // an explicit Cancel / drawer-close, or a route blocker bridged in by the
  // page/wizard container (useFormRouteLeave).
  const pendingLeave = useRef<(() => void) | null>(null);
  const pendingCancel = useRef<(() => void) | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);

  const requestLeave = useCallback(
    (onConfirmLeave: () => void, onCancelLeave?: () => void) => {
      if (isDirty) {
        pendingLeave.current = onConfirmLeave;
        pendingCancel.current = onCancelLeave ?? null;
        setDiscardOpen(true);
      } else {
        onConfirmLeave();
      }
    },
    [isDirty],
  );

  const keepEditing = useCallback(() => {
    const cancel = pendingCancel.current;
    pendingLeave.current = null;
    pendingCancel.current = null;
    setDiscardOpen(false);
    cancel?.();
  }, []);

  const confirmDiscard = useCallback(() => {
    const leave = pendingLeave.current;
    pendingLeave.current = null;
    pendingCancel.current = null;
    setDiscardOpen(false);
    // ORDER MATTERS: proceed the held navigation FIRST, then reset. Resetting
    // first clears isDirty, the route blocker un-arms (React Router releases a
    // blocker whose predicate stops blocking) and the stored proceed becomes a
    // no-op — the dialog closes but the navigation is silently lost. Leaving
    // first keeps the blocker held; the reset then reverts the values so a
    // closed-but-still-mounted container (a toggled drawer) stops arming the
    // beforeunload + route-leave guards over abandoned edits.
    leave?.();
    form.reset();
  }, [form]);

  const discardAndLeave = useCallback(
    (leave: () => void) => {
      leave();
      form.reset();
    },
    [form],
  );

  const discardDialog = (
    <ConfirmDialog
      open={discardOpen}
      onClose={keepEditing}
      onConfirm={confirmDiscard}
      title={t('forms.discardTitle')}
      description={t('forms.discardBody')}
      confirmLabel={t('forms.discard')}
    />
  );

  return {
    form,
    onFormSubmit: (e) => form.handleSubmit(onValid)(e),
    formId,
    line,
    refuse: line.refuse,
    reseed,
    gone: line.gone,
    isDirty,
    isSubmitting,
    requestLeave,
    discardAndLeave,
    discardDialog,
  };
}
